import { useMemo } from 'react';

import {
  difference,
  isEmpty,
  isNil,
  lowerCase,
  orderBy,
  sortBy,
  uniq,
} from 'lodash';
import { DateTime } from 'luxon';
import { useSelector } from 'react-redux';

import { OrganizationMember } from '@appTypes/esb';
import type { OrgPositionDetail } from '@appTypes/esb/organizations/positions/_orgGuid';
import { featureFlags } from '@modules/featureFlags/utils/featureFlags';
import {
  useGetOrgAdultsQuery,
  useGetOrgYouthsQuery,
  useGetUnitRosterAdultsQuery,
  useGetUnitRosterYouthsQuery,
} from '@modules/roster/api';
import {
  useGetMembershipRegistrationsQuery,
  useGetOrgPositionsQuery,
  useGetOrganizationProfileQuery,
} from '@modules/rtk/esb-api';
import { unitTypes } from '@shared/constants';
import { organizationGuidSel } from '@shared/duck/selectors/general.selectors';

import { RestrictedSBPositions } from '../constants';
import {
  RosterAdultMemberExtended,
  RosterYouthMemberExtended,
  getRequiredPermissionNames,
} from '../permissionsTab/utils';

export function useUnitRosterAdults() {
  const organizationGuid = useSelector(organizationGuidSel);
  const data = useGetUnitRosterAdultsQuery(
    {
      organizationGuid,
    },
    {
      skip: !organizationGuid,
    },
  );

  return data;
}

export function useOrgUnitRosterAdults() {
  const organizationGuid = useSelector(organizationGuidSel);
  const data = useGetOrgAdultsQuery(
    {
      organizationGuid,
      body: { includeRegistrationDetails: true, includeExpired: true },
    },
    {
      skip: !organizationGuid,
    },
  );

  return data;
}

export function useOrgPositions() {
  const organizationGuid = useSelector(organizationGuidSel);
  const data = useGetOrgPositionsQuery(
    {
      organizationGuid,
    },
    {
      skip: !organizationGuid,
    },
  );

  return data;
}

export function usePersonPermissionsPos(personGuid: string) {
  const organizationGuid = useSelector(organizationGuidSel);
  const { data, isFetching } = useGetMembershipRegistrationsQuery(
    {
      personGuid,
      body: { status: ['current'], organizationGuid },
    },
    {
      skip: !personGuid || !organizationGuid,
    },
  );
  if (!data) return { positions: [], loading: isFetching };

  const unitReqPos = getRequiredPermissionNames(data, 'unit');
  const subUnitReqPos = getRequiredPermissionNames(data, 'subUnit');
  const functionalPos = data
    .filter(pos => pos.isFunctional)
    .map(pos => pos.position);

  return {
    positions: [...unitReqPos, ...subUnitReqPos, ...functionalPos],
    loading: isFetching,
  };
}

export function useOrgProfile() {
  const organizationGuid = useSelector(organizationGuidSel);
  const data = useGetOrganizationProfileQuery(
    {
      orgGuid: organizationGuid,
    },
    {
      skip: isNil(organizationGuid) || isEmpty(organizationGuid),
    },
  );

  return data;
}

export function useUnitRosterAdultsWithPositions() {
  const organizationGuid = useSelector(organizationGuidSel);
  const {
    isFetching: loadingRosterAdults,
    data: rosterAdults = { users: [], unitType: '' },
  } = useGetUnitRosterAdultsQuery(
    {
      organizationGuid,
    },
    {
      skip: !organizationGuid,
    },
  );

  const {
    isFetching: loadingRosterYouths,
    data: rosterYouths = { users: [], unitType: '' },
  } = useGetUnitRosterYouthsQuery(
    {
      organizationGuid,
    },
    {
      skip: !organizationGuid,
    },
  );

  const { isFetching: loadingOrgAdults, data: orgAdults = { members: [] } } =
    useGetOrgAdultsQuery(
      {
        organizationGuid,
        body: { includeRegistrationDetails: true, includeExpired: true },
      },
      {
        skip: !organizationGuid,
      },
    );

  const { isFetching: loadingOrgYouths, data: orgYouths = { members: [] } } =
    useGetOrgYouthsQuery(
      {
        organizationGuid,
        body: { includeRegistrationDetails: true, includeExpired: true },
      },
      {
        skip: !organizationGuid,
      },
    );

  const {
    isFetching: loadingOrgPositions,
    data: orgPositions = { Positions: [] },
  } = useGetOrgPositionsQuery(
    {
      organizationGuid,
    },
    {
      skip: !organizationGuid,
    },
  );

  const adultsData: RosterAdultMemberExtended[] = useMemo(() => {
    if (isNil(rosterAdults?.users)) return [];
    return sortBy(rosterAdults.users, ['lastName', 'firstName']).map(user => {
      const userOrgPositions = orgPositions.Positions.filter(
        (pos: OrgPositionDetail) =>
          pos.personsAssigned.some(
            ({ personGuid }) => personGuid === user.personGuid,
          ),
      );

      const registrationData = orgAdults.members
        .filter(
          (member: OrganizationMember) =>
            member.personGuid === user.personGuid &&
            DateTime.fromISO(member.registrarInfo.registrationExpireDt).startOf(
              'day',
            ) > DateTime.now().startOf('day'),
        )
        .map(
          ({
            position,
            positionId,
            registrarInfo: { personPositionId, registrationExpireDt },
          }) => ({
            position,
            positionId,
            personPositionId,
            registrationExpireDt,
          }),
        );

      const allPositions = uniq([
        ...user.positions
          .map(pos => pos.position)
          .filter(pos =>
            featureFlags.getFlag('SBPD_222_REMOVE_PARTIAL_SB_POSITIONS')
              ? !RestrictedSBPositions.includes(pos)
              : pos,
          ),
        ...registrationData.map(pos => pos.position),
      ]);

      return {
        ...user,
        userOrgPositions,
        registrationData,
        allPositions,
        isExpired: !registrationData.length,
      };
    });
  }, [rosterAdults, orgPositions, orgAdults]);

  const youthsData: RosterYouthMemberExtended[] = useMemo(() => {
    if (isNil(rosterYouths?.users)) return [];
    return sortBy(rosterYouths?.users, ['lastName', 'firstName']).map(user => {
      const registrationData = orgYouths.members
        .filter(
          (member: OrganizationMember) =>
            member.personGuid === user.personGuid &&
            DateTime.fromISO(member.registrarInfo.registrationExpireDt) >
              DateTime.now().startOf('day'),
        )
        .map(
          ({
            position,
            positionId,
            registrarInfo: { personPositionId, registrationExpireDt },
          }) => ({
            position,
            positionId,
            personPositionId,
            registrationExpireDt,
          }),
        );

      const defaultYouthPosition: OrgPositionDetail = {
        positionLong: registrationData.length ? 'Youth Member' : '',
        PositionId: 0,
        assigned: 0,
        min: 0,
        max: 0,
        isFunctional: false,
        isPaid: false,
        isYouthPosition: false,
        minAge: 0,
        isKey3: false,
        isCBCRequired: false,
        personsAssigned: [],
      };

      const allPositions = uniq([
        ...user.positions.map(pos => pos.position),
        ...registrationData.map(pos => pos.position),
      ]);

      let sanitazedAllPositions = [...allPositions];
      if (
        rosterYouths.unitType === unitTypes.TROOP &&
        allPositions.includes('Den Chief')
      ) {
        sanitazedAllPositions = allPositions.filter(pos => pos !== 'Den Chief');
      }

      return {
        ...user,
        userOrgPositions: [defaultYouthPosition],
        registrationData,
        allPositions: sanitazedAllPositions,
        isExpired: !registrationData,
      };
    });
  }, [rosterYouths, orgYouths]);

  return {
    loading:
      loadingRosterAdults ||
      loadingOrgPositions ||
      loadingRosterYouths ||
      loadingOrgAdults ||
      loadingOrgYouths,
    adultsData,
    youthsData,
    unitType: rosterAdults.unitType,
  };
}

export function useRosterPermissions() {
  const { adultsData, youthsData } = useUnitRosterAdultsWithPositions();

  const resultFilteredData = useMemo(
    () =>
      orderBy(
        [...adultsData, ...youthsData],
        person => lowerCase(person.lastName),
        ['asc'],
      ),
    [adultsData, youthsData],
  );

  const unitRequiredRosterPositions = useMemo(
    () =>
      resultFilteredData
        .filter(person => !person?.isExpired)
        .reduce((curr, person) => {
          const permissions = getRequiredPermissionNames(
            [...person.positions, ...person.registrationData],
            'unit',
          );
          return { ...curr, [person.personGuid]: permissions };
        }, {} as Record<string, string[]>),
    [resultFilteredData],
  );

  const rosterAllPositions = useMemo(
    () =>
      resultFilteredData
        .filter(person => !person?.isExpired)
        .reduce(
          (curr, person) => ({
            ...curr,
            [person.personGuid]: person.allPositions,
          }),
          {} as Record<string, string[]>,
        ),
    [resultFilteredData],
  );

  const subUnitRequiredRosterPositions = useMemo(
    () =>
      resultFilteredData
        .filter(person => !person?.isExpired)
        .reduce((curr, person) => {
          const permissions = getRequiredPermissionNames(
            [...person.positions, ...person.registrationData],
            'subUnit',
          );
          // Positions records superposes subUnit, ex. Profile Editor SubUnit -> Profile Editor General
          const difer = difference(
            permissions,
            rosterAllPositions[person.personGuid],
          );
          return { ...curr, [person.personGuid]: difer };
        }, {} as Record<string, string[]>),
    [resultFilteredData, rosterAllPositions],
  );

  return {
    unitRequiredRosterPositions,
    rosterAllPositions,
    subUnitRequiredRosterPositions,
  };
}
